import { useEffect, useState } from "react";
import { Updater, useImmer } from "use-immer";

import { WebUiScopeType } from "@intelligems/ig-types";

import { useGetOrg } from "../../api/organizations/get-organization";
import { useIsAdmin } from "../../api/user-auths/is-admin";
import { getIsSubscriptionActive } from "../MainApp/Utils/dialogHelpers";

import { ADMINS_HAVE_FULL_ACCESS, FEATURE_TOGGLE_GLOBAL_DEBUG } from "./Scopes";

export const useScope = (features?: WebUiScopeType[]) => {
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const organization = useGetOrg();

  const isAdmin = useIsAdmin();

  useEffect(() => {
    if (!features) {
      setHasAccess(false);
    } else if (
      FEATURE_TOGGLE_GLOBAL_DEBUG ||
      features.some((permission) => permission === "debug")
    ) {
      setHasAccess(false);
    } else if (isAdmin) {
      setHasAccess(true);
    } else {
      setHasAccess(
        organization.data.org.configurations.some(
          (config) =>
            features.some((permission) => permission === config.key) &&
            config.value === true
        )
      );
    }
  }, [organization.data.org.configurations, features, isAdmin]);

  return hasAccess;
};

interface useHasAccessOptions {
  override: "enabled" | "disabled" | undefined;
  checkIntegrationStatus: boolean;
  checkSubscriptionStatus: boolean;
  debugId: string;
}

interface isDisabledOptions {
  isAdmin: boolean;
  overrideResultWith: "enabled" | "disabled" | undefined;
  isIntegrationActive: boolean | null;
  isSubscriptionActive: boolean | null;
  debugId: string;
}

// Ideally shouldn't be used outside of this file
const isDisabled = (hasAccess: boolean, options: isDisabledOptions) => {
  // Verbose to read more easily

  if (ADMINS_HAVE_FULL_ACCESS && options.isAdmin) {
    return false;
  }
  if (options.overrideResultWith !== undefined) {
    return options.overrideResultWith !== "enabled";
  }

  if (
    options.isIntegrationActive !== null &&
    options.isSubscriptionActive !== null
  ) {
    return !(
      options.isSubscriptionActive &&
      options.isIntegrationActive &&
      hasAccess
    );
  }

  if (options.isSubscriptionActive !== null) {
    return !(options.isSubscriptionActive && hasAccess);
  }

  if (options.isIntegrationActive !== null) {
    return !(options.isIntegrationActive && hasAccess);
  }

  return !hasAccess;
};

export const useHasAccess = (
  features: WebUiScopeType[],
  options: useHasAccessOptions
) => {
  const organization = useGetOrg();
  const hasScopeAccess = useScope(features);
  const isAdmin = useIsAdmin();

  const [isIntegrationActive, setIsIntegrationActive] =
    useState<boolean>(false);
  const [isSubscriptionActive, setIsSubscriptionActive] =
    useState<boolean>(false);

  useEffect(() => {
    setIsIntegrationActive(
      organization.data.org.integrationStatus === "active"
    );
    setIsSubscriptionActive(
      getIsSubscriptionActive(organization.data.org.organizationSubscriptions)
    );
  }, [
    organization.data.org.integrationStatus,
    organization.data.org.organizationSubscriptions,
  ]);

  const result = !isDisabled(hasScopeAccess, {
    isAdmin: isAdmin || false,
    isIntegrationActive: options.checkIntegrationStatus
      ? isIntegrationActive
      : null,
    isSubscriptionActive: options.checkSubscriptionStatus
      ? isSubscriptionActive
      : null,
    overrideResultWith:
      options.override !== undefined ? options.override : undefined,
    debugId: options.debugId,
  });
  return result;
};

type UseScopeModalOptions = {
  isEnabled: boolean;
  requiredShopifyScopes?: string[];
  modalPopupProps?: {
    title: string;
    message: string;
    yesButtonText?: string;
    noButtonText?: string;
    yesButtonAction?: () => void;
    noButtonAction?: () => void;
    showButtons?: boolean;
  };
  doAdminsHaveAccess: boolean;
  previousEnabled: boolean | undefined;
  setPreviousEnabled: Updater<boolean | undefined>;
};

export const useHasRequiredShopifyScopes = (requiredScopes: string[]) => {
  const organization = useGetOrg();
  const [hasRequiredScopes, setHasRequiredScopes] = useImmer<
    boolean | undefined
  >(undefined);

  useEffect(() => {
    const hasAllScopes = requiredScopes.every((scope) =>
      organization.data.org.scopes?.includes(scope)
    );
    setHasRequiredScopes(hasAllScopes);
  }, [organization.data.org.scopes, requiredScopes]);

  return hasRequiredScopes;
};
